<script lang="ts">
  import { run } from "svelte/legacy";

  import { onMount } from "svelte";
  import * as THREE from "three";
  import { GLTFLoader } from "../3d/GLTFLoader";
  import { OrbitControls } from "../3d/OrbitControls";
  import { FBXLoader } from "../3d/FBXLoader";

  interface Props {
    url?: string;
    width?: string;
    height?: string;
  }

  let { url = undefined, width = "100%", height = "400px" }: Props = $props();

  let container: HTMLDivElement = $state();
  let renderer: THREE.WebGLRenderer | undefined;
  let scene: THREE.Scene | undefined;
  let camera: THREE.PerspectiveCamera | undefined;
  let controls: OrbitControls | undefined;
  let model: THREE.Object3D | undefined;
  let loading: boolean = $state(false);
  let errorMsg: string = $state("");

  // TODO temporary hack enabling only gltf
  const isGLTF = (u: string) =>
    true || (!!u && /(\.gltf|\.glb)(\?.*)?$/i.test(u));
  const isFBX = (u: string) => !!u && /\.fbx(\?.*)?$/i.test(u);

  let loadModel: (u: string) => Promise<void>;

  loadModel = async (u: string) => {
    if(!u || u == "") {
      return;
    } else {
      console.log("Test " + u);
    }
    if (!scene) return;
    // remove previous
    if (model) {
      scene.remove(model);
      model.traverse((n) => {
        // dispose geometries and materials
        // @ts-ignore
        if (n.geometry) n.geometry.dispose?.();
        // @ts-ignore
        if (n.material) {
          // material can be an array
          const m: any = n.material;
          if (Array.isArray(m)) m.forEach((mm) => mm.dispose?.());
          else m.dispose?.();
        }
      });
      model = undefined;
    }

    if (!u) {
      loading = false;
      return;
    }

    loading = true;
    errorMsg = "";

    try {
      if (isGLTF(u)) {
        const loader = new GLTFLoader(undefined);
        const gltf: any = await loader.loadAsync(u);
        model = gltf.scene || gltf.scenes?.[0];
      } else if (isFBX(u)) {
        const loader = new FBXLoader();
        model = await loader.loadAsync(u);
      } else {
        throw new Error("Unsupported file type. Supported: .gltf, .glb, .fbx");
      }

      // center and scale the model
      const box = new THREE.Box3().setFromObject(model as THREE.Object3D);
      const size = box.getSize(new THREE.Vector3());
      const maxDim = Math.max(size.x, size.y, size.z);
      const desiredSize = 1.6; // target size in scene units
      const scale = maxDim > 0 ? desiredSize / maxDim : 1;
      model.scale.setScalar(scale);
      box.setFromObject(model);
      const center = box.getCenter(new THREE.Vector3());
      model.position.sub(center);
      scene.add(model);
    } catch (e: any) {
      console.error(e);
      errorMsg = e?.message || String(e);
    } finally {
      loading = false;
    }
  };

  function createRenderer() {
    if (!container) return;
    scene = new THREE.Scene();
    scene.background = new THREE.Color(0xffffff);

    const w = container.clientWidth || 300;
    const h = container.clientHeight || 300;
    camera = new THREE.PerspectiveCamera(45, w / h, 0.1, 1000);
    camera.position.set(0, 1.6, 3);

    renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(w, h);
    renderer.setPixelRatio(window.devicePixelRatio || 1);
    renderer.domElement.style.display = "block";
    container.appendChild(renderer.domElement);

    controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;

    const hemi = new THREE.HemisphereLight(0xffffff, 0x444444, 1.0);
    scene.add(hemi);
    const dir = new THREE.DirectionalLight(0xffffff, 0.8);
    dir.position.set(5, 10, 7.5);
    scene.add(dir);

    const animate = () => {
      requestAnimationFrame(animate);
      controls?.update();
      renderer?.render(scene as THREE.Scene, camera as THREE.Camera);
    };
    animate();

    const onResize = () => {
      if (!container || !renderer || !camera) return;
      const wi = container.clientWidth;
      const he = container.clientHeight;
      camera.aspect = wi / he;
      camera.updateProjectionMatrix();
      renderer.setSize(wi, he);
    };
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
    };
  }

  onMount(() => {
    const cleanupResize = createRenderer();
    // initial load
    loadModel(url || "");

    return () => {
      // cleanup
      cleanupResize?.();
      if (
        renderer &&
        renderer.domElement &&
        renderer.domElement.parentElement
      ) {
        renderer.domElement.parentElement.removeChild(renderer.domElement);
      }
      controls?.dispose();
      scene = undefined;
      camera = undefined;
      renderer = undefined;
      model = undefined;
    };
  });

  $effect(() => {
    loadModel(url);
  })

</script>

<div
  class="viewer-root"
  bind:this={container}
  style="width: {width}; height: {height};"
>
  {#if loading}
    <div class="viewer-overlay">Loading 3D model…</div>
  {:else if errorMsg}
    <div class="viewer-overlay">{errorMsg}</div>
  {/if}
</div>

<style>
  .viewer-root {
    position: relative;
    overflow: hidden;
  }
  .viewer-root canvas {
    width: 100%;
    height: 100%;
    display: block;
  }
  .viewer-overlay {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
  }
</style>
